<template>
  <div class="about-page">
    <MobileOnly>
      <AppContainer>
        <h1 class="mt-5 mb-0 text-center">{{ $tt('about_page.title') }}</h1>
      </AppContainer>
      <AboutPageImg class="header-img mb-5" edgy />
      <AppContainer class="mb-9">
        <AboutPageIntro />
      </AppContainer>
    </MobileOnly>

    <DesktopOnly>
      <AppContainer>
        <AppSection top class="about">
          <AppRow wide>
            <div>
              <h1 class="mb-7">{{ $tt('about_page.title') }}</h1>
              <AboutPageIntro />
            </div>
            <AboutPageImg />
          </AppRow>
        </AppSection>
      </AppContainer>
    </DesktopOnly>

    <AppContainer>
      <AppSection class="mission">
        <h2 class="mb-8" v-html="$tt('about_page.our_mission')" />
        <h3 class="text-center mb-7">{{ $tt('about_page.aim') }}</h3>
        <FeatureTriad :items="missionItems" carousel />
      </AppSection>

      <AppSection>
        <AppThirdLayout class="items-center">
          <PassportStampVideo />
          <h3 v-html="$tt('about_page.successfully_processed')" class="video-text" />
        </AppThirdLayout>
      </AppSection>

      <AppSection class="choose">
        <h2 class="mb-8" v-html="$tt('about_page.choose')" />
        <AppThirdLayout
          v-for="{ title, src, alt, p1, p2 } in chooseItems"
          class="choose-item-container items-center"
        >
          <div>
            <AppImg :src :alt />
          </div>
          <div>
            <h3>{{ title }}</h3>
            <div class="choose-item-content flex gap-7">
              <p class="equal">• {{ p1 }}</p>
              <p class="equal">• {{ p2 }}</p>
            </div>
          </div>
        </AppThirdLayout>
      </AppSection>
    </AppContainer>

    <AboutBranches />
    <AppContainer>
      <AppSection class="our-story">
        <h2 class="mb-8" v-html="$tt('about_page.our_story_title')" />
        <AppThirdLayout>
          <DesktopOnly>
            <AppImg src="/images/about/rocking-chair-with-cat-on-table-computer.jpg" :alt="$tt('images.rocking_chair_with_cat_and_laptop')" />
          </DesktopOnly>
          <div class="me-10">
            <p class="lead mb-6">{{ $tt('about_page.our_story_1') }}</p>
            <MobileOnly>
              <AppImg src="/images/about/rocking-chair-with-cat-on-table-computer.jpg" :alt="$tt('images.rocking_chair_with_cat_and_laptop')" />
            </MobileOnly>
            <p>{{ $tt('about_page.our_story_2') }}</p>
          </div>
        </AppThirdLayout>
      </AppSection>
    </AppContainer>
    <AboutCompanyInfo />
  </div>
</template>

<script setup lang="ts">
import { calcCompanyActiveYears } from '~/helpers/date';

const { tt } = useLocalization();
const yearsActive = calcCompanyActiveYears();
usePageHead({
  title: tt('about_page.title'),
  description: tt('about_page.meta_desc'),
  alternateBranches: 'all',
});
useSchemaOrg('about');

const missionItems = [
  {
    src: '/images/shields/checkmark.jpg',
    alt: tt('about_page.img_checkmark_shield'),
    title: tt('about_page.aim_1'),
  },
  {
    src: '/images/about/gem-icon.jpg',
    alt: tt('about_page.img_gem'),
    title: tt('about_page.aim_2'),
  },
  {
    src: '/images/about/docement-with-check-mark-icon.jpg',
    alt: tt('about_page.img_doc_with_checkmark'),
    title: tt('about_page.aim_3'),
  }
];

const chooseItems = [
  {
    src: '/images/about/magnifying-glass-and-doc-icon.jpg',
    alt: tt('about_page.img_magnifying_glass'),
    title: tt('about_page.trust_title'),
    p1: tt('about_page.trust_1', { yearsActive }),
    p2: tt('about_page.trust_2')
  },
  {
    src: '/images/about/fast-online-service-icon.jpg',
    alt: tt('about_page.img_fast_online_service'),
    title: tt('about_page.fast_and_secure_title'),
    p1: tt('about_page.fast_and_secure_1'),
    p2: tt('about_page.fast_and_secure_2')
  },
  {
    src: '/images/about/support-headphones-and-blue-crown-icon.jpg',
    alt: tt('about_page.img_support_headphones'),
    title: tt('about_page.customer_support_title'),
    p1: tt('about_page.customer_support_1'),
    p2: tt('about_page.customer_support_2')
  }
];
</script>

<style scoped lang="scss">
p {
  font-size: 20px;
}
.lead {
  font-size: 24px;
  font-weight: 500;
}
.choose {
  img {
    margin-inline-start: var(--space-8);
  }
}
video {
  width: 100%;
  pointer-events: none;
}

@media (max-width: 850px) {
  p {
    font-size: 18px;
  }
  .lead {
    font-size: 20px !important;
  }
  h1 {
    font-size: 36px;
  }
  h1, h2 {
    text-align: center;
    margin-bottom: var(--space-7) !important;
  }
  h3 {
    font-size: 20px;
  }
  .me-10 {
    margin-inline-end: 0 !important;
  }
  .header-img {
    display: block;
  }
  .about {
    padding-top: var(--space-6) !important;
  }
  .mission {
    p {
      font-size: 24px;
    }
  }
  .choose {
    img {
      width: 100%;
      max-width: 180px;
      margin-inline-start: 0 !important;
    }
  }
  video {
    width: 100%;
  }
  .video-text {
    text-align: center;
  }
  .choose h3 {
    text-align: center;
    font-size: 24px;
  }
  .choose p {
    font-size: 16px;
  }
  .choose-item-container {
    margin-top: var(--space-7);
  }
  .choose-item-content {
    flex-direction: column;
    gap: 0 !important;
    p:not(:last-child) {
      margin-bottom: 0 !important;
    }
  }
  .our-story {
    padding-top: 0;
    img {
      width: 100%;
    }
    p:not(.lead) {
      font-size: 16px;
    }
  }
}
</style>